import { Radio } from "@base-ui/react/radio";
import { RadioGroup } from "@base-ui/react/radio-group";
import { type Currency, formatAmount } from "@loot-os/api-contract";
import { useId, useState } from "react";

import { Alert, type AlertTone } from "../core/Alert.js";
import { Button } from "../core/Button.js";
import { Input } from "../core/Input.js";
import { Modal } from "../core/Modal.js";
import { Skeleton } from "../core/Skeleton.js";
import { Text } from "../core/Text.js";
import { cn } from "../foundations/cn.js";
import { Amount } from "./Amount.js";

/** One top-up pack: paid in real money, credited in the store's currency. */
export interface DepositPack {
  id: string;
  name: string;
  /** What it adds to the balance, in the store's cents. */
  creditedCents: number;
  /** What it costs, in `priceCurrency`'s cents. */
  priceCents: number;
  /** Starts paying for it (a redirect to checkout). */
  onBuy: () => void;
}

/** The packs on sale, or why none can be bought. */
export type DepositPacksState =
  | { status: "loading" }
  /** Buying is off, or nothing is on sale: the words say which. */
  | { status: "unavailable"; message: string }
  | {
      status: "ready";
      /** The amounts' group name. */
      label: string;
      packs: readonly DepositPack[];
      priceCurrency: Currency;
      /** "Balance after" beside the total. */
      afterLabel: string;
      /** The button's words for the picked pack's formatted price: (price) => `Deposit ${price}`. */
      buyLabel: (price: string) => string;
      /** The pack whose checkout is starting; null otherwise. */
      buying: string | null;
      buyingLabel: string;
      /** Nothing can be bought right now (a lock, a purchase being confirmed). */
      disabled: boolean;
    };

/** The gift code field: what was typed, and what the last redemption said. */
export interface GiftCodeField {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  submitLabel: string;
  pending: boolean;
  disabled: boolean;
  /** The last redemption: what it added (`success`), or why it didn't (`danger`). */
  outcome: { text: string; tone: "neutral" | "success" | "warning" | "danger" } | null;
}

export interface DepositModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Add funds". */
  title: string;
  closeLabel?: string;
  /** The store's currency, the balance's and what each pack adds. */
  currency: Currency;
  balance: {
    label: string;
    /** Spendable cents; null until the balance loads. */
    cents: number | null;
    /** Why some of it is held, when any is. */
    held?: string;
  };
  /** How things stand: a lock, a checkout that didn't start, a payment coming back. */
  notices: readonly { id: string; text: string; tone: AlertTone }[];
  deposit: DepositPacksState;
  giftCode: GiftCodeField;
}

/**
 * Adding to the balance: the balance, the store's top-up packs
 * as amount chips, the balance after the picked one and one Deposit button that starts checkout,
 * then a gift code field. Money stays calm: neutral mono amounts, one primary action. A sheet
 * from the bottom on phones, centered from sm (Modal).
 */
export function DepositModal({
  open,
  onOpenChange,
  title,
  closeLabel,
  currency,
  balance,
  notices,
  deposit,
  giftCode,
}: DepositModalProps) {
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={title} closeLabel={closeLabel}>
      <div className="-mt-3 flex flex-col gap-1">
        <Text variant="body-b2" tone="secondary" className="flex items-center gap-2">
          {balance.label}
          {balance.cents === null ? (
            <span className="font-mono text-text-tertiary">—</span>
          ) : (
            <Amount cents={balance.cents} currency={currency} size="meta" />
          )}
        </Text>
        {balance.held && (
          <Text variant="body-b2" tone="secondary">
            {balance.held}
          </Text>
        )}
      </div>
      {notices.map((notice) => (
        <Alert key={notice.id} tone={notice.tone}>
          {notice.text}
        </Alert>
      ))}
      <DepositPacks deposit={deposit} balanceCents={balance.cents} currency={currency} />
      <div className="border-t border-border-subtle pt-4">
        <GiftCodeForm {...giftCode} />
      </div>
    </Modal>
  );
}

function DepositPacks({
  deposit,
  balanceCents,
  currency,
}: {
  deposit: DepositPacksState;
  balanceCents: number | null;
  currency: Currency;
}) {
  if (deposit.status === "loading") {
    return (
      <div className="grid grid-cols-3 gap-2" aria-hidden>
        <Skeleton className="h-16 rounded-field" />
        <Skeleton className="h-16 rounded-field" />
        <Skeleton className="h-16 rounded-field" />
      </div>
    );
  }
  if (deposit.status === "unavailable") {
    return (
      <Text variant="body-b2" tone="secondary">
        {deposit.message}
      </Text>
    );
  }
  return <PackPicker deposit={deposit} balanceCents={balanceCents} currency={currency} />;
}

function PackPicker({
  deposit,
  balanceCents,
  currency,
}: {
  deposit: Extract<DepositPacksState, { status: "ready" }>;
  balanceCents: number | null;
  currency: Currency;
}) {
  const labelId = useId();
  const [picked, setPicked] = useState(deposit.packs[0]?.id ?? "");
  const pack = deposit.packs.find((p) => p.id === picked) ?? deposit.packs[0];
  const busy = deposit.buying !== null;
  const disabled = deposit.disabled || busy;
  return (
    <div className="flex flex-col gap-4">
      <span id={labelId} className="sr-only">
        {deposit.label}
      </span>
      <RadioGroup
        aria-labelledby={labelId}
        value={pack?.id}
        onValueChange={(next) => setPicked(String(next))}
        disabled={disabled}
        className="grid grid-cols-3 gap-2 sm:grid-cols-4"
      >
        {deposit.packs.map((p) => (
          <Radio.Root
            key={p.id}
            value={p.id}
            className={cn(
              "flex min-w-0 cursor-pointer flex-col items-center gap-0.5 rounded-field border border-border-default bg-bg-raised px-2 py-3 text-text-primary",
              "not-data-disabled:hover:bg-bg-hover data-checked:border-accent-default data-checked:bg-accent-subtle",
              "data-disabled:cursor-default data-disabled:opacity-60",
              "outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-focus-ring",
            )}
          >
            <Amount cents={p.priceCents} currency={deposit.priceCurrency} size="lg" />
            <span className="max-w-full truncate font-body text-body-b2 text-text-secondary">
              {p.name}
            </span>
          </Radio.Root>
        ))}
      </RadioGroup>
      {pack && (
        <>
          <div className="flex items-baseline justify-between gap-3">
            <Text variant="body-b2" tone="secondary">
              {deposit.afterLabel}
            </Text>
            {balanceCents === null ? (
              <span className="font-mono text-text-tertiary">—</span>
            ) : (
              <Amount cents={balanceCents + pack.creditedCents} currency={currency} size="lg" />
            )}
          </div>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            disabled={disabled}
            loading={deposit.buying === pack.id}
            onClick={pack.onBuy}
          >
            {deposit.buying === pack.id
              ? deposit.buyingLabel
              : deposit.buyLabel(formatAmount(pack.priceCents, deposit.priceCurrency))}
          </Button>
        </>
      )}
    </div>
  );
}

/** Redeems a gift code into the balance, and says what it added or why it didn't. */
function GiftCodeForm({
  label,
  placeholder,
  value,
  onChange,
  onSubmit,
  submitLabel,
  pending,
  disabled,
  outcome,
}: GiftCodeField) {
  const refused = outcome?.tone === "danger";
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="flex items-end gap-2">
        <Input
          label={label}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={64}
          mono
          invalid={refused}
          disabled={disabled}
          className="min-w-0 flex-1"
        />
        <Button
          type="submit"
          variant="secondary"
          disabled={disabled || pending || value.trim() === ""}
          loading={pending}
        >
          {submitLabel}
        </Button>
      </div>
      {outcome && (
        <Text
          variant="body-b2"
          tone={outcome.tone === "neutral" ? "secondary" : outcome.tone}
          role={refused ? "alert" : "status"}
        >
          {outcome.text}
        </Text>
      )}
    </form>
  );
}
